import { calcularEstado, ESTADOS_VIGENTE_AMPLIO } from './calcularEstado';

// Copia deliberada de las mismas fechas/constantes que ya existen en
// MantenimientosView.js (hoyMedianoche/diasHasta/ESTADOS_CAPTACION/
// necesitaAgendarVisita/esMesActual) — no se tocan esas definiciones
// originales (archivo de ~6000 líneas, con muchos otros usos de esas mismas
// funciones fuera de los KPIs) para no arriesgar una regresión ahí. Si algún
// día diverge el criterio, hay que actualizar los dos sitios a la vez.
function hoyMedianoche() { const h = new Date(); h.setHours(0, 0, 0, 0); return h; }
function diasHasta(dateStr) { if (!dateStr) return 999; return Math.round((new Date(dateStr + 'T00:00:00') - hoyMedianoche()) / 86400000); }

const ESTADOS_CAPTACION = ['sin_contrato', 'contactado', 'interesado', 'sin_respuesta', 'contrato_enviado', 'no_interesado'];

const DIAS_VISITA_PENDIENTE = 30;
function necesitaAgendarVisita(m) {
  return !!(m.tieneContrato && m.modoCobro === 'visita' &&
    !['pendiente_agendar', 'mantenimiento_agendado', 'pendiente_renovar', 'pendiente_reagendar'].includes(m.estado) &&
    !ESTADOS_CAPTACION.includes(m.estado) && m.estado !== 'baja' &&
    m.fechaProximoMantenimiento && diasHasta(m.fechaProximoMantenimiento) <= DIAS_VISITA_PENDIENTE);
}

function esMesActual(s) {
  if (!s) return false;
  const d = new Date(s + 'T12:00:00'), n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth();
}

function esHoy(s) {
  if (!s) return false;
  const d = new Date(s + 'T12:00:00'), n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}

// Misma transformación que la línea `data = useMemo(...)` de MantenimientosView.js
// — clasifica el estado real de cada contrato (calcularEstado) preservando el
// estado tal cual estaba guardado en `_storedEstado`.
export function mapEstados(raw) {
  return raw.map(m => ({ ...m, _storedEstado: m.estado, estado: calcularEstado(m) }));
}

// Misma lógica que el `kpis = useMemo(...)` de MantenimientosView.js (TabAnalitica)
// — única fuente de verdad para estos conteos, reutilizada también por el
// dashboard de atención al cliente. Recibe `data` YA pasado por mapEstados().
export function calcularKpisMantenimientos(data) {
  // Antes 'vigentes' (tarjeta "Contratos vigentes") solo miraba
  // ['activo','vence_pronto'] mientras que 'todosActivos' (ARR, modo de
  // cobro) usaba los 10 estados de ESTADOS_VIGENTE_AMPLIO — dos bases
  // distintas para lo que en pantalla parecía el mismo concepto de
  // "cartera". Unificado en un único conjunto.
  const vigentes        = data.filter(m => ESTADOS_VIGENTE_AMPLIO.includes(m.estado));
  const pendienteFirmar = data.filter(m => m.estado === 'contrato_enviado');
  const sepaDevueltos   = data.filter(m => m.estado === 'sepa_devuelto');
  const pendAgendar     = data.filter(m => m.estado === 'pendiente_agendar');
  const visitasProg     = data.filter(m => m.estado === 'mantenimiento_agendado');
  const pendRenovar     = data.filter(m => m.estado === 'pendiente_renovar');
  const pendReagendar   = data.filter(m => m.estado === 'pendiente_reagendar');
  // OJO: fechaInicioContrato se pone ya en 'contrato_enviado' (al registrar la
  // llamada como "firmado", antes de que el cliente firme de verdad) — usar esa
  // fecha aquí contaría como "nuevo" un contrato que el cliente luego no llega
  // a firmar. fechaPrimeraFirma (o fechaFirma en registros previos a ese campo)
  // solo se escribe en procesarFirmaPublica.js, en el momento real de la firma.
  const nuevosEste      = data.filter(m => esMesActual(m.fechaPrimeraFirma || m.fechaFirma));
  const firmadosHoy     = data.filter(m => esHoy(m.fechaPrimeraFirma || m.fechaFirma));
  const sepaGenerados   = data.filter(m => m.estado === 'sepa_generado');
  const pendFactura     = data.filter(m => m.estado === 'pendiente_factura');
  const sinRespuesta    = data.filter(m => m.estado === 'sin_respuesta');
  const aristonSinId    = data.filter(m => m.extensionGarantia && !m.idContratoAriston && !ESTADOS_CAPTACION.includes(m.estado));
  const vencidos        = data.filter(m => m.estado === 'vencido');
  const vencePronto     = data.filter(m => m.estado === 'vence_pronto');
  // Contratos "en visita" con la próxima visita pendiente de agendar — ver
  // necesitaAgendarVisita() (misma función que usa el badge por fila en
  // MantenimientosView.js).
  const visitaPendiente = data.filter(necesitaAgendarVisita);
  // Contratos vigentes sin fechaVencimientoContrato — sin este dato,
  // calcularEstado() no puede avisar de "vence pronto"/"vencido".
  const sinFechaVencimiento = data.filter(m =>
    m.tieneContrato && !m.fechaVencimientoContrato &&
    !ESTADOS_CAPTACION.includes(m.estado) && m.estado !== 'baja'
  );
  const modoVisita      = vigentes.filter(m => m.modoCobro === 'visita');
  const modoSepa        = vigentes.filter(m => m.modoCobro !== 'visita');

  const arr            = vigentes.reduce((s, m) => s + (m.precioAnual || 0), 0);
  const pendCobrarEur  = sepaGenerados.reduce((s, m) => s + Math.round((m.precioAnual || 0) * 1.21 * 100) / 100, 0);
  const devueltosEur   = sepaDevueltos.reduce((s, m) => s + Math.round((m.precioAnual || 0) * 1.21 * 100) / 100, 0);
  const renovarEur     = pendRenovar.reduce((s, m) => s + Math.round((m.precioAnual || 0) * 1.21 * 100) / 100, 0);

  // Contratos "en riesgo" (mismo criterio que arrEnRiesgo en PanelDireccion,
  // DashboardView.js) — aquí se expone también la lista, no solo el conteo,
  // para poder mostrar antigüedad en el dashboard de atención al cliente.
  const enRiesgo = data.filter(m => ['pendiente_renovar', 'vencido', 'sepa_devuelto'].includes(m.estado));

  return {
    totalVigentes: vigentes.length,
    pendienteFirmar: pendienteFirmar.length,
    pendienteFirmarLista: pendienteFirmar,
    sepaDevueltos: sepaDevueltos.length,
    devueltosEur,
    pendAgendar: pendAgendar.length,
    visitasProg: visitasProg.length,
    pendRenovar: pendRenovar.length,
    pendRenovarLista: pendRenovar,
    pendReagendar: pendReagendar.length,
    renovarEur,
    nuevosEste: nuevosEste.length,
    firmadosHoy: firmadosHoy.length,
    sepaGenerados: sepaGenerados.length,
    pendCobrarEur,
    pendFactura: pendFactura.length,
    sinRespuesta: sinRespuesta.length,
    aristonSinId: aristonSinId.length,
    vencidos: vencidos.length,
    vencePronto: vencePronto.length,
    venceProntoLista: vencePronto,
    visitaPendiente: visitaPendiente.length,
    sinFechaVencimiento: sinFechaVencimiento.length,
    modoVisita: modoVisita.length,
    modoSepa: modoSepa.length,
    mrr: arr / 12,
    arr,
    enRiesgo: enRiesgo.length,
    enRiesgoLista: enRiesgo,
  };
}
